import {throwError as observableThrowError,  Observable ,  from as fromPromise, of } from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpParams } from '@angular/common/http';
import Raven from 'raven-js';
import { config } from 'config';
import { appSetting } from 'config/appSettings';
import { GigyaSessionService } from './gigya-session.service';
import { AppHelperService } from './app-helper.service';
import { AuthService } from './auth.service';

@Injectable()
export class CustomHttpInterceptor implements HttpInterceptor {
    constructor(
        private gigyaSessionService: GigyaSessionService,
        private appHelperService: AppHelperService,
        private readonly authService: AuthService,
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        let newClonedReq;
        let reqObj;
        if(req.method=='GET' && this.isTimestampNeeded(req.url)){
            const timestamp = new Date().getTime();
            let timestampQueryParam;
            if(req.url.indexOf('?') !== -1){
                timestampQueryParam = '&timestamp=' + timestamp;
            } else {
                timestampQueryParam = '?timestamp=' + timestamp; 
            }  
            reqObj = {
                setHeaders:{
                    [config.customReqHeaders.appVersion.key] : config.customReqHeaders.appVersion.value
                },
                url : req.url.concat(timestampQueryParam)
            }  
            reqObj = this.addRoleHeaderToRequestObject(reqObj);          
        } else {
            //Standard CORS requests do not send or set any cookies by default.
            //In order to set/send cookie we need to set the .withCredentials property to true.
            reqObj = {
                setHeaders:{
                    [config.customReqHeaders.appVersion.key] : config.customReqHeaders.appVersion.value
                }
            }    
            reqObj = this.addRoleHeaderToRequestObject(reqObj);  
        }
        newClonedReq = req.clone(reqObj);

        Raven.captureBreadcrumb({
            message: '[Custom] captured new angular http request...',
            category: 'angular-http',
            data: newClonedReq,
            level: 'info'
        });

        //send the newly created request
        return next.handle(newClonedReq).pipe(
        catchError((errorObj) => {
            return this.handleError(newClonedReq, next, errorObj);
        })) as any;
    }

    isTimestampNeeded(url) {
        const isFile = url.substring(url.length - 5, url.length);
        // Caching of all files except html is avoided in order to make sure changes are reflected everytime.
        if (isFile === '.html') {
            return false;
        }
        return true;
    } 

    addRoleHeaderToRequestObject(reqObj) {
        let userData : any = sessionStorage.getItem(appSetting.sessionStorage.userAccount);
        if(userData) {                
            userData = JSON.parse(userData);
            if(userData.role) reqObj.setHeaders[config.customReqHeaders.userRole.key] = userData.role;
            if(userData.extUserId) reqObj.setHeaders[config.customReqHeaders.userId.key] = userData.extUserId;
        }
        return reqObj;
    }
    // Interceptor Error Handling 
    handleError(newClonedReq, next, errorObj, tryReLogin = true) {
        Raven.captureBreadcrumb({
            message: '[Custom] captured failed angular http request...',
            category: 'angular-http',
            data: errorObj,
            level: 'error'
        });
        if(errorObj.status === 403 && errorObj.error.code === 'CLASS_DELETED'){
            sessionStorage.setItem('cupa.c1.classInvitationError.classDeleted', 'true');
            this.appHelperService.showLoaderAndNavigateToDashboard();
        }
        if(tryReLogin && errorObj.status == 401 && errorObj.error.errorCode == 'AUTHORIZATION_ERROR' ){
            // if user is unauthorized try to login again in dls session using gigya data.
            return this.unAuthorizedUserReLogin(newClonedReq, next);
        } else if (errorObj.status == 503 || (errorObj.status == 403 && errorObj.error.code == 'STALE_REQUEST')) {
            window.location.reload();
        } else if(errorObj.status == 401 && errorObj.error.errorCode == 'USER_MISMATCH_ERROR') {
            sessionStorage.removeItem(appSetting.sessionStorage.userAccount);
            window.location.reload();
        } else if (errorObj.status == 0) {
            //Update the errorObj with custom Error to override the cyclic reference.
            var customErrorObj = new Error(errorObj.message);
            customErrorObj['status'] = errorObj.status;
            errorObj = customErrorObj;
        }
        //return the error to the method that called it
        return observableThrowError(errorObj);
    }

    /**
     * Get userData from gigya and send the Clone of failed request appending userData in QueryParams
     * The userData is base64 encoded.
     * 
     * @param {*} newClonedReq
     * @param {*} next
     * @returns {Observable} Clone of Request with userData appended to QueryParams for session initialization
     * @memberof CustomHttpInterceptor
     */
    unAuthorizedUserReLogin(newClonedReq, next) {
        return fromPromise(this.getUserDataFromGigya())
                .pipe(switchMap(userData => {
                    return next.handle(newClonedReq.clone(this.addUserDataToQueryParams(newClonedReq, userData)))
                    .pipe(catchError((errorObj) => {
                        return this.handleError(newClonedReq, next, errorObj, false);
                    })) as any;
                }))
    }

    addUserDataToQueryParams(req, userData) {
        let newParams = new HttpParams({fromString: req.params.toString()});
        //Encode the string into base64 encoding
        let data = btoa(encodeURIComponent(JSON.stringify(userData)));
        //Encode the param to handle the following characters: , / ? : @ & = + $ #
        newParams = newParams.append('userData', encodeURIComponent(data));
        return {params : newParams};
    }

    async getUserDataFromGigya(){
        const gigyaData = await this.gigyaSessionService.getUserData();
        if(gigyaData) {
            const userData:any = {
                extUserId : gigyaData.UID,
                role : gigyaData.c1UserRole,
                email : gigyaData.profile.email,
                firstName : gigyaData.profile.firstName,
                lastName : gigyaData.profile.lastName
            }
            if( gigyaData.profile.email.indexOf('@cambridge.org')!= -1){
                const samlData:any  = await this.getUserSAMLData();
                if(samlData){
                  if(samlData.eltCustomerServices == "true" || samlData.eltSuperAdmin == "true" || samlData.eltSales == "true"){
                    userData.samlData = samlData;
                  } else{
                    window['gigya'].accounts.logout();
                  }
                }
            }
            return userData;
        } else {
            this.gigyaSessionService.gigyaLogout();
            this.appHelperService.navigateToOnboardingApp(['login'], { forceExit: true });
            return Promise.reject("Gigya session not found.");
        }
    }
    async getUserSAMLData(){
        return new Promise((resolve)=>{
            window['gigya'].accounts.getAccountInfo({
                extraProfileFields: 'samlData',
                callback: (res)=>{
                    resolve(res.profile.samlData);
                }
            })
        });
    }
}